import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { DefaultChipList } from '@omnea-app/frontend/components/shared/ui/chip/DefaultChipList'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { RowGroupingColumnTableCell } from '../../row-grouping/components/RowGroupingColumnTableCell'
import { ROW_GROUPING_NONE_GROUPING_TEXT_VALUE } from '../../row-grouping/helpers'
import { BaseColumnConfig } from '../types'
import { baseColumnConfig } from './baseColumnConfig'
import { ColumnHeaderWithTooltip } from './helpers/ColumnHeaderWithTooltip'
import type { ChipProps } from '@mui/material'
import type { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import type { GRID_ID_AUTOGENERATED } from '@mui/x-data-grid/hooks/features/rows/gridRowsUtils'
import type { GridValidRowModel } from '@mui/x-data-grid/models/gridRows'
import type { RefObject } from 'react'

type GroupingRow = {
  [GRID_ID_AUTOGENERATED]: string
}

export type ChipMultipleColumnConfig<T extends GridValidRowModel> = BaseColumnConfig<T, 'chip-multiple'> & {
  columnType: 'chip-multiple'
  getItems: (params: GridRenderCellParams<T>) => { name: string; color?: ChipProps['color'] }[] | undefined
  maxChips?: number
  headerTooltip?: string
  // Real valueGetter signature doesn't reflect that it can be used for grouping valueGetter as well
  // On the list to be addressed in v9: https://github.com/mui/mui-x/issues/17846
  valueGetter: (value: unknown, row: T | GroupingRow, column: GridColDef<T>, apiRef: RefObject<GridApiPremium>) => unknown
}

const defaultMaxChips = 2

export const getMultiChipColumn = <T extends GridValidRowModel>({
  style,
  headerTooltip,
  ...config
}: ChipMultipleColumnConfig<T>): GridColDef<T> => ({
  ...baseColumnConfig,
  ...style,
  ...config,
  ...(headerTooltip !== undefined && {
    renderHeader: () => <ColumnHeaderWithTooltip label={config.headerName} tooltip={headerTooltip} />,
  }),
  groupingValueGetter: config.valueGetter,
  renderCell: (params) => {
    if (!isDefined(params.value)) return null
    if (params.rowNode.type === 'group') {
      return (
        <RowGroupingColumnTableCell
          columnHeaderName={config.headerName}
          value={params.value || ROW_GROUPING_NONE_GROUPING_TEXT_VALUE}
          id={params.id}
        />
      )
    }
    return (
      <DefaultChipList
        {...(config?.getDataTestId ? { ['data-testid']: config.getDataTestId(params) } : {})}
        maxChips={config.maxChips ?? defaultMaxChips}
        chips={(config.getItems(params) ?? []).map((item) => ({
          label: item.name,
          color: item.color ?? 'neutral',
        }))}
      />
    )
  },
})
